import { apiClient as api } from "./httpClient";

// CRUD dos catálogos de status da agenda (RN-114): confirmação e presença, com cor.
// Escopo controlado: só edição de cor e ativar/desativar (renomear quebraria o casamento
// por nome com o histórico de agendamentos, que guarda o texto do status).

export interface ScheduleStatusRow {
  id: string;
  name: string;
  color: string | null;
  itemIndex: number;
  active: boolean;
  /** só na presença: se o atendimento é cobrado (option set ChargeForService) */
  chargeForService?: boolean;
  /** só na confirmação: finalizador — encerra o agendamento e bloqueia presença (RN-114b) */
  isTerminal?: boolean;
  /** significado para o produto (RN-114d): o nome é livre, o kind é o que o sistema reconhece */
  kind?: string | null;
  /** quem o Sininho avisa pelo chat quando um agendamento entra neste status (RN-551f) */
  notifyTarget?: string | null;
  /** texto do aviso com variáveis (RN-551g); nulo = texto padrão */
  notifyMessage?: string | null;
  /** sessão neste status entra no formulário de evolução (RN-542i) */
  allowsEvolution?: boolean;
}

/** Significados possíveis por catálogo (RN-114d). Vazio = sem significado especial. */
export const PRESENCE_KINDS: { value: string; label: string }[] = [
  { value: "", label: "Nenhum" },
  { value: "attended", label: "Atendido (paciente foi atendido)" },
  { value: "no_show", label: "Falta (paciente não veio)" },
];

export const CONFIRMATION_KINDS: { value: string; label: string }[] = [
  { value: "", label: "Nenhum" },
  { value: "confirmed", label: "Confirmado" },
  { value: "cancelled_patient", label: "Cancelado pelo paciente" },
  { value: "cancelled_professional", label: "Cancelado pelo profissional" },
  { value: "cancelled_clinic", label: "Cancelado pela clínica (feriado, recesso)" },
];

/** Destino do aviso do Sininho pelo chat (RN-551f). Vazio = sem aviso. */
export const NOTIFY_TARGETS: { value: string; label: string }[] = [
  { value: "", label: "Ninguém" },
  { value: "professional", label: "Profissional do agendamento" },
  { value: "reception", label: "Recepção" },
];

export const notifyLabel = (target?: string | null): string =>
  NOTIFY_TARGETS.find((t) => t.value === (target ?? ""))?.label ?? "Ninguém";

export const kindLabel = (catalog: "confirmation" | "presence", kind?: string | null): string =>
  (catalog === "presence" ? PRESENCE_KINDS : CONFIRMATION_KINDS).find((k) => k.value === (kind ?? ""))?.label ?? "Nenhum";

export interface ScheduleStatusesResult {
  confirmation: ScheduleStatusRow[];
  presence: ScheduleStatusRow[];
}

export interface UpdateScheduleStatusPayload {
  color: string | null;
  active: boolean;
  /** presença: cobra atendimento */
  chargeForService?: boolean;
  /** confirmação: finalizador */
  isTerminal?: boolean;
  /** significado (RN-114d); omitir ou nulo = nenhum */
  kind?: string | null;
  /** aviso pelo chat (RN-551f): omitir mantém; "" limpa */
  notifyTarget?: string | null;
  /** texto do aviso (RN-551g): omitir mantém; "" volta ao padrão */
  notifyMessage?: string | null;
  /** permite evolução (RN-542i): omitir mantém */
  allowsEvolution?: boolean;
}

export interface CreateScheduleStatusPayload {
  name: string;
  color: string | null;
  active: boolean;
  /** presença: cobra atendimento */
  chargeForService?: boolean;
  /** confirmação: finalizador */
  isTerminal?: boolean;
  /** significado (RN-114d) */
  kind?: string | null;
  /** aviso pelo chat (RN-551f) */
  notifyTarget?: string | null;
  /** texto do aviso (RN-551g) */
  notifyMessage?: string | null;
  /** permite evolução (RN-542i): omitir = padrão (cancelamento não, o resto sim) */
  allowsEvolution?: boolean;
}

/** Prazos da agenda da entidade, em dias (RN-118k). */
export interface ScheduleWindows {
  /** Dias APÓS a data do agendamento em que ele ainda pode ser editado. */
  editLockDays: number;
  /** Até quantos dias PARA TRÁS é possível criar ou mover um agendamento. */
  retroactiveDays: number;
}

/** Matriz perfil × ação da agenda (RN-115d): só as seis permissões da agenda. */
export interface SchedulePermissionColumn {
  permission: string;
  title: string;
}

export interface SchedulePermissionProfileRow {
  profileId: string;
  name: string;
  granted: string[];
}

export interface SchedulePermissionsMatrix {
  permissions: SchedulePermissionColumn[];
  profiles: SchedulePermissionProfileRow[];
}

export const scheduleStatusesService = {
  getPermissions: async (): Promise<SchedulePermissionsMatrix> => {
    const response = await api.get("/schedule-statuses/permissions");
    return response.data;
  },

  setPermission: async (profileId: string, permission: string, granted: boolean): Promise<void> => {
    await api.put("/schedule-statuses/permissions", { profileId, permission, granted });
  },

  getWindows: async (): Promise<ScheduleWindows> => {
    const response = await api.get("/schedule-statuses/windows");
    return response.data;
  },

  setWindows: async (payload: ScheduleWindows): Promise<void> => {
    await api.put("/schedule-statuses/windows", payload);
  },

  getAll: async (): Promise<ScheduleStatusesResult> => {
    const response = await api.get("/schedule-statuses");
    return response.data;
  },

  createConfirmation: async (payload: CreateScheduleStatusPayload): Promise<void> => {
    await api.post("/schedule-statuses/confirmation", payload);
  },

  createPresence: async (payload: CreateScheduleStatusPayload): Promise<void> => {
    await api.post("/schedule-statuses/presence", payload);
  },

  updateConfirmation: async (id: string, payload: UpdateScheduleStatusPayload): Promise<void> => {
    await api.put(`/schedule-statuses/confirmation/${id}`, payload);
  },

  updatePresence: async (id: string, payload: UpdateScheduleStatusPayload): Promise<void> => {
    await api.put(`/schedule-statuses/presence/${id}`, payload);
  },
};
